'use client';

import clsx from 'clsx';
import { Children, createContext, useContext, type ReactElement, type ReactNode, type UIEventHandler } from 'react';
import {
  Collection as RACCollection,
  Header as RACHeader,
  Menu as RACMenu,
  type MenuProps as RACMenuProps,
  MenuItem as RACMenuItem,
  type MenuItemProps as RACMenuItemProps,
  MenuSection as RACMenuSection,
  type MenuSectionProps as RACMenuSectionProps,
  MenuTrigger as RACMenuTrigger,
  type MenuTriggerProps as RACMenuTriggerProps,
  SubmenuTrigger as RACSubmenuTrigger,
  Text as RACText,
} from 'react-aria-components';
import { IconCheckBold, IconChevronRight } from '../../icons/index.js';
import { isProduction } from '../../utils/is-production.js';
import { CheckboxIndicator } from '../internal/checkbox-indicator/checkbox-indicator.js';
import { CollapsibleList } from '../internal/collapsible-list/collapsible-list.js';
import type { CollectionItem, CollectionProps, Key, NavigationProps, StringLikeChildren } from '../types.js';
import { textStyle } from '../typography/utils.js';

const SubmenuContext = createContext(false);

export interface MenuRootProps extends Pick<RACMenuTriggerProps, 'onOpenChange' | 'isOpen' | 'defaultOpen'> {
  /** The menu trigger with its associated menu. Provide the trigger as the first child, and the menu as the second child. */
  children: ReactNode;
}

/**
 * Encapsulates a menu trigger and its associated menu. The trigger can be any Cimpress UI button, and the menu will be displayed when the trigger is activated.
 *
 * See [menu usage guidelines](https://ui.cimpress.io/components/menu/).
 */
export function MenuRoot(props: MenuRootProps) {
  return <RACMenuTrigger {...props} />;
}

MenuRoot.displayName = 'MenuRoot';

export interface SubmenuRootProps {
  /** The submenu trigger with its associated menu. Provide the item trigger as the first child, and the menu as the second child. */
  children: ReactElement[];
}

/**
 * Encapsulates a submenu trigger and its associated menu. The trigger must be a `MenuItem`, and the menu will be displayed when the trigger is hovered or focused.
 *
 * See [menu usage guidelines](https://ui.cimpress.io/components/menu/).
 */
export function SubmenuRoot({ children, ...props }: SubmenuRootProps) {
  const [trigger, menu] = Children.toArray(children) as ReactElement[];

  return (
    <RACSubmenuTrigger {...props} delay={0}>
      {trigger!}
      <SubmenuContext.Provider value={true}>{menu!}</SubmenuContext.Provider>
    </RACSubmenuTrigger>
  );
}

SubmenuRoot.displayName = 'SubmenuRoot';

export interface MenuProps<T extends CollectionItem = CollectionItem>
  extends CollectionProps<T>, Pick<RACMenuProps<T>, 'selectedKeys' | 'onSelectionChange' | 'selectionMode'> {
  /**
   * The alignment of the menu overlay relative to its trigger.
   * @default 'start'
   */
  align?: 'start' | 'end';
  /** Handler that is called when an item is selected. When using `onAction`, all items must have an `id` prop defined. */
  onAction?: (key: Key) => void;
  /** Handler that is called when the list of items is scrolled. */
  onScroll?: UIEventHandler<HTMLDivElement>;
}

/** Displays a collapsible list of options and actions that users can choose from. */
export function Menu<T extends CollectionItem>({ align = 'start', ...props }: MenuProps<T>) {
  if (!isProduction() && props.items != null && typeof props.children !== 'function') {
    console.warn('`Menu` requires `children` to be a function when using `items` prop');
  }

  const isSubmenu = useContext(SubmenuContext);

  return (
    <CollapsibleList
      placement={isSubmenu ? 'end top' : `bottom ${align}`}
      offset={isSubmenu ? -1 : 5} // 1px border + 4px actual offset
      crossOffset={isSubmenu ? -8 : undefined}
      maxHeight={315}
    >
      <RACMenu {...props} className="cim-menu" />
    </CollapsibleList>
  );
}

Menu.displayName = 'Menu';

export interface MenuSectionProps<T extends CollectionItem = CollectionItem>
  extends CollectionProps<T>, Pick<RACMenuSectionProps<T>, 'selectedKeys' | 'onSelectionChange' | 'selectionMode'> {
  /** The ID of the section. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the section title. */
  title: string;
}

/** Groups list items within `Menu` into a section. */
export function MenuSection<T extends CollectionItem>({ title, children, items, ...props }: MenuSectionProps<T>) {
  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`MenuSection` requires `children` to be a function when using `items` prop');
  }

  return (
    <RACMenuSection {...props} className="cim-menu-section">
      <RACHeader className={clsx('cim-menu-section-header', textStyle({ variant: 'small-semibold', tone: 'subtle' }))}>
        {title}
      </RACHeader>
      <RACCollection items={items}>{children}</RACCollection>
    </RACMenuSection>
  );
}

MenuSection.displayName = 'MenuSection';

export interface MenuItemProps
  extends NavigationProps, Pick<RACMenuItemProps, 'isDisabled' | 'onAction' | 'onHoverStart' | 'onHoverEnd'> {
  /** The ID of the item. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the label. */
  children: StringLikeChildren;
  /** Additional description for the menu item. */
  description?: string;
  /** An icon representing the menu item. */
  icon?: ReactNode;
}

/** Renders a single list item within `Menu`. */
export function MenuItem({ children, description, icon, ...props }: MenuItemProps) {
  const textValue =
    typeof children === 'string' ? children : typeof children === 'number' ? String(children) : children.join('');

  return (
    <RACMenuItem {...props} className="cim-menu-item" textValue={textValue}>
      {({ isDisabled, isSelected, selectionMode, hasSubmenu }) => (
        <>
          {selectionMode === 'single' && <IconCheckBold UNSAFE_className="cim-menu-item-selection-indicator" />}
          {selectionMode === 'multiple' && <CheckboxIndicator isSelected={isSelected} isDisabled={isDisabled} />}

          {icon}

          <div className="cim-menu-item-text">
            <RACText slot="label" className={textStyle({ variant: 'body', tone: isDisabled ? 'muted' : 'base' })}>
              {children}
            </RACText>

            {description && (
              <RACText
                slot="description"
                className={textStyle({ variant: 'small', tone: isDisabled ? 'muted' : 'subtle' })}
              >
                {description}
              </RACText>
            )}
          </div>

          {hasSubmenu && <IconChevronRight size={24} UNSAFE_className="cim-menu-item-submenu-indicator" />}
        </>
      )}
    </RACMenuItem>
  );
}

MenuItem.displayName = 'MenuItem';
